/**
 * QUIPWA — deck uploads through the outbox
 *
 * Editing a deck, adding a card from a note or importing a file writes the
 * deck's local `collection.db` first — that is what sessions read — and then
 * queues one `deck.upload`. The handler packs the current collection as a
 * `.wflash` and uploads it, creating the file the first time.
 *
 * Before, each of those places uploaded by itself: offline, the change never
 * reached Drive (nothing retried it), and a deck imported offline had no
 * content at all on the device.
 *
 * ```ts
 * await deckUploads.queue(deckInstanceId)   // after any local change to the deck
 * ```
 *
 * Media files (`media/<sha256>`) are not stored in the collection, only in the
 * `.wflash`. A re-pack carries them over from the previous file on Drive or,
 * for a deck imported offline, from the original bytes kept until the first
 * upload (`keepOriginal`).
 */
import JSZip from 'jszip'
import type { CollectionDBService } from '../db/collection-db-service'
import type { IdbBlobStore } from '../db/idb-blob-store'
import type { StateDBService } from '../db/state-db-service'
import { isDriveError } from '../drive/drive-errors'
import type { DriveFile, DriveService } from '../drive/drive-service'
import type { KvStore } from '../local/kv-store'
import { KV } from '../local/kv-store'
import type { Outbox } from '../local/outbox'
import type { DeckInstance } from '../../types/domain'
import type { ImportResult, WFlashImporter } from '../wflash/wflash-importer'

export const DECK_UPLOAD = 'deck.upload'
export const WFLASH_MIME = 'application/octet-stream'

const originalKey = (deckInstanceId: string) => `QUIPWA:wflash-pending:${deckInstanceId}`
const toSeconds = (iso: string) => Math.floor(new Date(iso).getTime() / 1000)

export interface PackInput {
  instance: Pick<DeckInstance, 'deck_id' | 'deck_name' | 'imported_at'>
  collectionDb: ArrayBuffer
  cardCount: number
  /** A previous .wflash whose `media/` entries must survive the re-pack. */
  previous?: ArrayBuffer | null
  now?: number
}

/** Package a collection as a `.wflash`: manifest + collection.db (+ carried-over media). */
export async function packWflash({ instance, collectionDb, cardCount, previous, now = Date.now() }: PackInput): Promise<ArrayBuffer> {
  const zip = new JSZip()
  let mediaCount = 0
  if (previous) {
    const old = await JSZip.loadAsync(previous)
    for (const [path, entry] of Object.entries(old.files)) {
      if (entry.dir || !path.startsWith('media/')) continue
      zip.file(path, await entry.async('uint8array'))
      mediaCount++
    }
  }
  const ts = Math.floor(now / 1000)
  zip.file('manifest.json', JSON.stringify({
    wflash_version: '1.0',
    schema_version: 1,
    deck_id: instance.deck_id,
    deck_name: instance.deck_name,
    description: null,
    created_at: instance.imported_at,
    updated_at: ts,
    card_count: cardCount,
    note_count: cardCount,
    media_count: mediaCount,
  }, null, 2))
  zip.file('collection.db', collectionDb)
  const bytes = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' })
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

export interface DeckUploadsDeps {
  drive: Pick<DriveService, 'ensureRootFolder' | 'createFile' | 'updateFileContent' | 'downloadFile'>
  state: Pick<StateDBService, 'getDeckInstance' | 'markDeckUploaded' | 'updateCardCount'>
  outbox: Pick<Outbox, 'enqueue' | 'find' | 'remove'>
  kv: KvStore
  blobs: Pick<IdbBlobStore, 'get' | 'put' | 'delete'>
  getCollection: (deckInstanceId: string) => Promise<CollectionDBService>
  /** After queueing (the sync engine debounces). */
  onWrite?: () => void
  now?: () => number
}

export class DeckUploads {
  private readonly deps: DeckUploadsDeps

  constructor(deps: DeckUploadsDeps) {
    this.deps = deps
  }

  /** The deck changed locally: upload it on the next sync. */
  async queue(deckInstanceId: string): Promise<void> {
    await this.deps.outbox.enqueue(DECK_UPLOAD, deckInstanceId, { deckInstanceId })
    this.deps.onWrite?.()
  }

  /** Keep an imported file's bytes until its first upload (its media live only there). */
  async keepOriginal(deckInstanceId: string, wflash: ArrayBuffer): Promise<void> {
    await this.deps.blobs.put(originalKey(deckInstanceId), wflash)
  }

  /** The deck was deleted: drop its queued upload and any kept original. */
  async forget(deckInstanceId: string): Promise<void> {
    await this.deps.outbox.remove(DECK_UPLOAD, deckInstanceId)
    await this.deps.blobs.delete(originalKey(deckInstanceId))
  }

  async hasPendingUpload(deckInstanceId: string): Promise<boolean> {
    return (await this.deps.outbox.find(DECK_UPLOAD, deckInstanceId)) !== null
  }

  /** Outbox handler. Throws to let the outbox retry later. */
  async push(deckInstanceId: string): Promise<void> {
    const { drive, state } = this.deps
    const instance = await state.getDeckInstance(deckInstanceId)
    if (!instance) return // deleted meanwhile; its file is trashed by the delete

    const collection = await this.deps.getCollection(deckInstanceId)
    await collection.flush()
    const cardCount = await collection.getCardCount()

    const original = await this.deps.blobs.get(originalKey(deckInstanceId))
    let previous: ArrayBuffer | null = original
    if (!previous && instance.drive_file_id && (await collection.getMediaCount()) > 0) {
      previous = await drive.downloadFile(instance.drive_file_id).catch((err: unknown) => {
        if (isDriveError(err, 'not_found')) return null
        throw err
      })
    }

    const wflash = await packWflash({
      instance, collectionDb: await collection.exportBuffer(), cardCount, previous, now: (this.deps.now ?? Date.now)(),
    })

    let file: DriveFile | null = null
    if (instance.drive_file_id) {
      try {
        file = await drive.updateFileContent(instance.drive_file_id, wflash, WFLASH_MIME)
      } catch (err) {
        if (!isDriveError(err, 'not_found')) throw err // gone: upload it again below
      }
    }
    file ??= await drive.createFile({
      name: `${instance.deck_name}.wflash`,
      parentId: await drive.ensureRootFolder(),
      mimeType: WFLASH_MIME,
      content: wflash,
    })

    await state.markDeckUploaded(deckInstanceId, file.id, toSeconds(file.modifiedTime))
    await state.updateCardCount(deckInstanceId, cardCount)
    // This device already holds this version: DeckSync must not download it back.
    if (file.version) await this.deps.kv.set(KV.deckVersion(deckInstanceId), file.version)
    if (original) await this.deps.blobs.delete(originalKey(deckInstanceId))
  }
}

/**
 * Import a `.wflash` (picked by the user, or generated from a CSV) on this
 * device: state rows, the card content in the local collection, and an upload
 * queued for the next sync. Works offline; nothing waits for Drive.
 */
export async function importDeckLocally(deps: {
  importer: Pick<WFlashImporter, 'importFromArrayBuffer'>
  getCollection: (deckInstanceId: string) => Promise<CollectionDBService>
  uploads: Pick<DeckUploads, 'keepOriginal' | 'queue'>
}, wflash: ArrayBuffer): Promise<ImportResult> {
  const result = await deps.importer.importFromArrayBuffer(wflash, null)
  if (result.errors.length > 0) return result
  const collection = await deps.getCollection(result.deck_instance_id)
  await collection.replaceFromWflash(wflash)
  await collection.flush()
  await deps.uploads.keepOriginal(result.deck_instance_id, wflash)
  await deps.uploads.queue(result.deck_instance_id)
  return result
}